/**
 * "Neuigkeiten" after signing in — one card at a time (Reiselust's pattern).
 *
 * A stack rather than a list: five items in a column is a wall of text that
 * gets scrolled past. **Oldest first**, so the stack ends on the newest
 * change; going back is offered from the second card on. **Nothing is
 * acknowledged before the last card**: closing half way means seeing it all
 * again next time, which is honest; marking things read that nobody read is
 * the failure the feature exists to prevent. What is acknowledged is exactly
 * what was shown (`items`), never what the server named — a newer server's
 * entry this bundle has no words for stays pending (news/news.ts).
 *
 * The card takes its position as props and holds no state, so a test can
 * render card three of five and read its button.
 */
import { useState } from 'react';
import { formatDate, tx, type NewsItem } from '@fsv/data';
import { Sheet } from '../components/Sheet';
import './news.css';

export function WhatsNew({
  items,
  onDone,
  onClose,
}: {
  items: readonly NewsItem[];
  onDone: () => Promise<void> | void;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const item = items[index];
  if (!item) return null;
  const finish = async () => {
    setBusy(true);
    try {
      await onDone();
    } finally {
      // Gone either way: a notice that will not close because the network
      // hiccuped is worse than one shown twice, and the next start offers it again.
      setBusy(false);
      onClose();
    }
  };
  return (
    <Sheet
      title={tx.news.title}
      kicker={tx.news.position(index + 1, items.length)}
      onClose={onClose}
    >
      <WhatsNewCard
        item={item}
        index={index}
        count={items.length}
        busy={busy}
        onBack={() => setIndex((i) => i - 1)}
        onNext={() => setIndex((i) => i + 1)}
        onDone={() => void finish()}
      />
    </Sheet>
  );
}

/** One card. No state: everything it shows is in its props. */
export function WhatsNewCard({
  item,
  index,
  count,
  busy = false,
  onBack,
  onNext,
  onDone,
}: {
  item: NewsItem;
  index: number;
  count: number;
  busy?: boolean;
  onBack: () => void;
  onNext: () => void;
  onDone: () => void;
}) {
  const last = index === count - 1;
  return (
    <article className="news-card" key={item.id}>
      <h3 className="news-card__title">{item.title}</h3>
      <p className="news-card__date mono">{formatDate(item.date)}</p>
      <p className="news-card__body">{item.body}</p>
      <div className="news-card__actions">
        {index > 0 && (
          <button type="button" className="btn" onClick={onBack} disabled={busy}>
            {tx.news.back}
          </button>
        )}
        <button
          type="button"
          className="btn btn--primary news-card__next"
          onClick={last ? onDone : onNext}
          disabled={busy}
        >
          {last ? tx.news.dismiss : tx.news.next}
        </button>
      </div>
      <p className="news-card__hint">{tx.news.laterHint}</p>
    </article>
  );
}
